/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
"use clent";
import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import qs from "qs";

// @ts-ignore
// eslint-disable-next-line import/no-relative-packages
import { message } from "../aspenUI/index";
import Bugsnag from "@bugsnag/js";
import { addPendingRequest, removePendingRequest } from "./adapterTools";
import {
  addLoginRedirect,
  convertJsonToString,
  cookieUtil,
  DEVICE_ID,
  DOMAIN_GREEN,
  DOMAIN_GREEN_PARTNER,
  EN,
  ERROR,
  ERROR_CODE,
  ERROR_MSG,
  formatBugsnagMessage,
  getCookie,
  generateStorageKey,
  getSource,
  i18nUtil,
  isClient,
  LANGUAGE,
  PUBLIC_PATHS,
  NOT_LOGIN_CODE,
  ONLY_MOBILE,
  PROFILE_PATHS,
  sendToApp,
  USER_AUTH,
  USER_ROLE,
  WALLET_PATHS,
  AGENT_OPERATING_TOKEN,
  JWT_TOKEN,
} from "@aspen/libs";
import { aspenApi as api } from "./aspen/api";
import Router from "next/router";
import { host } from "@adgm/cross-config";

let netErrorList: string[] = [];
const errorSize = 10;
const cancelTokenSource = axios.CancelToken.source();

axios.defaults.withCredentials = true;
axios.defaults.headers["Content-Type"] = "application/x-www-form-urlencoded";
axios.defaults.headers["Accept-Aspen-Language"] = "aspen_en";
// const isKXFKSDKDS = process?.env?.NEXT_PUBLIC_REFSFGADEF ?? "";
const _baseURL = host;
const service: AxiosInstance = axios.create({
  // 非生产环境使用ip+port+魔法header访问，允许跨域
  baseURL: _baseURL,
  timeout: 2 * 60 * 1000,
});

const uploadNetWorkError = (err: any) => {
  const message = formatBugsnagMessage(convertJsonToString(err));
  //简单的网络错误不要重复上传
  if (message.includes("Network Error") || message.includes("timeout of")) {
    const re = /\[XHR\]--path: .+?(?=--Error)/gim;
    const simpleError = message.match(re)?.[0];
    //将简化版网络错误放入栈中
    simpleError && netErrorList.push(simpleError);
    if (netErrorList.length >= errorSize) {
      //上传错误
      //错误栈满了,重置错误栈
      Bugsnag.notify(new Error(netErrorList.join(" ; \n")));
      netErrorList = [];
    }
    return;
  }
  Bugsnag.notify(new Error(message));
};

const _checkAndhandleReqHeaders: (config: AxiosRequestConfig) => AxiosRequestConfig = config => {
  // 处理post请求的参数
  if (
    config.method &&
    (config.method.toLowerCase() == "post" || config.method.toLowerCase() == "put") &&
    config.headers &&
    config.headers["Content-Type"] == "application/x-www-form-urlencoded"
  ) {
    config.data = qs.stringify(config.data);
  }
  // 配置蓝绿请求头参数
  if (
    (isClient &&
      (location.href.includes(DOMAIN_GREEN) || location.href.includes(DOMAIN_GREEN_PARTNER))) ||
    process.env.NEXT_PUBLIC_COLOR_ENV == "green"
  ) {
    if (config.headers) config.headers["traffic"] = "green";
  }

  if (isClient && config.headers) {
    const jwtToken = window.sessionStorage.getItem(generateStorageKey(JWT_TOKEN)) ?? "";
    // 设置请求头jwtToken
    // 注册之后绑定手机操作，获取验证码的时候，调用接口需要jwtToken，在业务场景出单独传参headers
    config.headers.jwtToken = config.headers?.jwtToken ?? jwtToken;

    // xsrf安全认证
    const xsrfToken = getCookie("XSRF-TOKEN") ?? "";
    config.headers["X-XSRF-TOKEN"] = xsrfToken;

    // 上报设备id
    const deviceId = window.localStorage.getItem(DEVICE_ID) ?? "";
    config.headers["device_id"] = deviceId;

    // 请求头添加语言
    config.headers["language"] = window.localStorage.getItem(LANGUAGE) ?? EN;

    // 请求头添加source
    config.headers.source = getSource();

    // 请求头添加agentOperatingToken/customerManagerOperatingToken
    const agentToken = generateStorageKey(AGENT_OPERATING_TOKEN);
    if (window.sessionStorage.getItem(agentToken)) {
      const agentOperatingToken = window.sessionStorage.getItem(agentToken) ?? "";
      if (window.localStorage.getItem(USER_AUTH) === USER_ROLE.ROLE_PARTNER_CUSTOMER) {
        config.headers.agentOperatingToken = agentOperatingToken;
      } else if (
        window.localStorage.getItem(USER_AUTH) === USER_ROLE.ROLE_MANAGER_CUSTOMER ||
        window.localStorage.getItem(USER_AUTH) === USER_ROLE.ROLE_MANAGER_READONLY
      ) {
        config.headers.customerManagerOperatingToken = agentOperatingToken;
      }
    }

    // manager 只读权限时，不可进行其它操作，只能查询用户信息
    const intl = i18nUtil.t();
    if (window.localStorage.getItem(USER_AUTH)) {
      if (
        config.method &&
        config.method.toLowerCase() == "post" &&
        window.localStorage.getItem(USER_AUTH) === USER_ROLE.ROLE_MANAGER_READONLY &&
        !location.href.includes(WALLET_PATHS.WALLET_ADDACCOUNT) &&
        !location.href.includes(PROFILE_PATHS.PROFILE_KYC_VERIFICATION)
      ) {
        // 取消请求 提示用户无权限
        const errorMsg = { message: intl["client.request.is.not.authorized"] };
        config.cancelToken = cancelTokenSource.token;
        cancelTokenSource.token.reason = errorMsg;
        cancelTokenSource.cancel(intl["client.request.is.not.authorized"]);
      }
    }
  }
  return config;
};

const _handleResNotify: (res: AxiosResponse) => void = res => {
  //  下载月结账单接口，res?.data是ArrayBuffer，不需要上报
  const isBuffer = res?.data instanceof ArrayBuffer;
  // ERROR_CODE 排除错误上报的code集合  ERROR_MSG 错误上报的信息集合
  const isErrorNoneValid =
    isBuffer || res?.data?.code == "0" || ERROR_CODE[res?.data?.code] == String(res?.data?.msg);
  const isErrorMsg = ERROR_MSG.some(msg => res?.data?.msg?.includes(msg));
  if (!isErrorNoneValid || isErrorMsg) {
    uploadNetWorkError(res);
  }
};

// @ts-ignore
const _handleErrorRes: (error: AxiosError) => Promise<any> = error => {
  const { response } = error;
  const intl = i18nUtil.t();
  const errorMsgTips = intl["global.error.tip"];
  if (response) {
    // 上报请求错误
    uploadNetWorkError(response);

    const { error, status, code, message: errMessage, path } = response.data as any;
    let errMsg = errorMsgTips;
    if (error || code) {
      errMsg = `${error ?? code}: ${status || intl[errMessage] || errMessage} for ${path}`;
    }
    // 临时方案：阿里无痕验证请求头太大导致400，请求返回400并且是登陆接口时不弹错误
    if (!(response?.status == 400 && response.request.responseURL.includes(api.customer.login))) {
      message.error(errMsg);
    }
    return Promise.reject({ msg: errMsg });
  }
  // 没有返回错误或者请求取消不上报
  if (
    !error ||
    Object.getPrototypeOf(error).__CANCEL__ ||
    (error && error?.message?.includes("Request aborted"))
  )
    return;

  // 上报请求错误
  uploadNetWorkError(error);

  const errmsg = intl?.[error?.message] || error?.message || errorMsgTips;
  message.error(errmsg);
  return Promise.reject({ msg: errmsg, error });
};

// 请求拦截器
service.interceptors.request.use(
  // @ts-ignore
  (config: AxiosRequestConfig) => {
    // 把当前请求信息添加到pendingRequest对象中
    addPendingRequest(config);
    // 本地或者配置了NEXT_PUBLIC_HOST_REFSFGADEF=KXFKSDKDS的环境，添加魔法header请求头 FIXME: 因为后端prod 401问题 目前全部打开此headers
    // if (process.env.NODE_ENV != "production" || isKXFKSDKDS) {
    if (config.headers) config.headers.REFSFGADEF = "KXFKSDKDS";
    // }
    return _checkAndhandleReqHeaders(config);
  },
  error => {
    return Promise.reject(error);
  },
);

// 响应拦截器
service.interceptors.response.use(
  // 请求成功
  (res: AxiosResponse) => {
    // 未登陆 , -1: 客户未登陆 -3: 代理操作超时
    if (NOT_LOGIN_CODE.includes(Number(res?.data?.code))) {
      if (ONLY_MOBILE) {
        //app传入的身份信息过期,需要app端登录
        sendToApp({ type: "notLogin" });
      }

      const _domain = res?.config?.headers?.domain ?? "";
      _domain && cookieUtil.remove(`${_domain}${JWT_TOKEN}`);

      if (isClient && !window.location.href.includes("login")) {
        window.localStorage.setItem(ERROR, "logout.out");
        // const _loginPath = getCurrentLoginPath(_domain);
        addLoginRedirect();
        Router.replace(PUBLIC_PATHS.LOGIN);
      } else {
        return Promise.reject({
          code: res?.data?.code,
        });
      }

      // 如果某个接口提示未登陆，则取消已经发送的请求
      removePendingRequest();
      //防止调用then错误弹窗提示
      return Promise.reject("logout.out");
    }
    // msg长度超过100时，返回"System error"
    if (res?.data?.msg?.length > 500) {
      message.error("System error");
      return Promise.reject({
        msg: "System error",
      });
    }

    // 缓存数据到本地
    if (isClient) {
      // _saveDataToLocalStorage(res);
    }

    // 上报错误处理
    _handleResNotify(res);

    return res;
  },
  // 请求失败
  (error: AxiosError) => {
    // 请求已发出，但是不在2xx的范围
    return _handleErrorRes(error);
  },
);

export default service;
